/**
 * custom-header.js
 * Behaviour for sections/custom-header.liquid
 *
 * Responsibilities:
 *  - Scroll-hide / show header (with instant re-appear on scroll-up)
 *  - [data-scrolled] attribute for CSS transitions (transparent → solid)
 *  - Mega-menu open / close with hover-bridge
 *  - Dropdown keyboard support
 *  - Mobile drawer toggle (delegates to the existing Stiletto drawer JS)
 *  - Cart count live update via Shopify Section Rendering API
 *
 * One file, no dependencies beyond the Shopify theme JS already loaded.
 */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────────
     HELPERS
  ────────────────────────────────────────────────────────────── */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ──────────────────────────────────────────────────────────────
     INIT — one instance per header section
  ────────────────────────────────────────────────────────────── */
  function initHeader(header) {
    if (!header) return;

    const sectionId   = header.dataset.sectionId;
    const megaWrap    = document.getElementById('csh-mega-' + sectionId);
    const megaBridge  = document.getElementById('csh-mega-bridge-' + sectionId);
    const forceScrolled = header.hasAttribute('data-force-scrolled');

    // Placeholder — overwritten by the mega-menu block if megaWrap exists
    let closeMegaOnScroll = function () {};

    /* ── SCROLL BEHAVIOUR ──────────────────────────────────────── */
    let lastScrollY   = window.scrollY;
    let ticking       = false;
    const SCROLL_THRESHOLD = 60; // px before the header goes "scrolled"

    // Secondary style: pin scrolled state immediately, no transparent phase
    if (forceScrolled) {
      header.setAttribute('data-scrolled', 'true');
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateScroll);
        ticking = true;
      }
    }

    function updateScroll() {
      ticking = false;
      const current   = window.scrollY;
      const scrolling  = current > SCROLL_THRESHOLD;
      const scrollingDown = current > lastScrollY;

      // Scrolled state (transparent → solid)
      // forceScrolled: always keep data-scrolled set regardless of scroll position
      if (forceScrolled || scrolling) {
        header.setAttribute('data-scrolled', 'true');
      } else {
        header.removeAttribute('data-scrolled');
      }

      // Hide header while scrolling down (past threshold), instant-show scrolling up
      if (scrollingDown && current > 200) {
        header.classList.add('is-hidden');
        header.classList.remove('is-instant');
      } else {
        header.classList.add('is-instant');
        header.classList.remove('is-hidden');
        // Remove is-instant after one frame so the re-hide transition is slow again
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            header.classList.remove('is-instant');
          });
        });
      }

      // Close mega menu on any scroll
      closeMegaOnScroll();

      lastScrollY = current;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    // Trigger once on load in case page is already scrolled (e.g. refresh)
    updateScroll();

    /* ── MEGA MENU ─────────────────────────────────────────────── */
    if (megaWrap) {
      const megaToggle  = $('[data-mega-toggle]', header);
      const megaPanel   = $('.csh__mega', megaWrap);
      const megaBackdrop = $('[data-mega-backdrop]', megaWrap);
      let megaOpen = false;
      let bridgeTimeout = null;

      function openMega() {
        if (megaOpen) return;
        // Position BEFORE adding is-open so the panel starts in the right place
        positionMega();
        positionBridge();
        megaOpen = true;
        megaWrap.classList.add('is-open');
        megaWrap.setAttribute('aria-hidden', 'false');
        if (megaToggle) megaToggle.setAttribute('aria-expanded', 'true');
        if (megaBridge) megaBridge.classList.add('is-active');
      }

      function closeMega() {
        if (!megaOpen) return;
        megaOpen = false;
        megaWrap.classList.remove('is-open');
        megaWrap.setAttribute('aria-hidden', 'true');
        if (megaToggle) megaToggle.setAttribute('aria-expanded', 'false');
        if (megaBridge) megaBridge.classList.remove('is-active');
      }

      // Allow updateScroll (defined above) to close the mega on scroll
      closeMegaOnScroll = closeMega;

      function positionMega() {
        // Position directly below the main nav row (not the whole header,
        // which includes the large pre-scroll logo on homepage)
        var mainRow = header.querySelector('.csh__main');
        var rect = (mainRow || header).getBoundingClientRect();
        megaWrap.style.top = rect.bottom + 'px';
      }

      function positionBridge() {
        if (!megaBridge || !megaToggle) return;
        var mainRow   = header.querySelector('.csh__main');
        var mainRect  = (mainRow || header).getBoundingClientRect();
        var toggleRect = megaToggle.getBoundingClientRect();
        megaBridge.style.top    = toggleRect.bottom + 'px';
        megaBridge.style.height = (mainRect.bottom - toggleRect.bottom + 4) + 'px';
      }

      // Set initial position on load so first open has no flash
      positionMega();

      if (megaToggle) {
        megaToggle.addEventListener('mouseenter', openMega);
        megaToggle.addEventListener('click', function () {
          megaOpen ? closeMega() : openMega();
        });
      }

      // Close mega when hovering over any other nav item (not the mega trigger itself)
      $$('.csh__item:not(.csh__item--has-mega)', header).forEach(function (item) {
        item.addEventListener('mouseenter', function () {
          clearTimeout(bridgeTimeout);
          closeMega();
        });
      });

      // Close when hovering out of toggle + bridge + panel
      [header, megaWrap, megaBridge].forEach(function (el) {
        if (!el) return;
        el.addEventListener('mouseleave', function () {
          bridgeTimeout = setTimeout(closeMega, 120);
        });
        el.addEventListener('mouseenter', function () {
          clearTimeout(bridgeTimeout);
        });
      });

      // Close on backdrop click
      if (megaBackdrop) {
        megaBackdrop.addEventListener('click', closeMega);
      }

      // Close on Escape
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && megaOpen) {
          closeMega();
          if (megaToggle) megaToggle.focus();
        }
      });

      // Close when keyboard focus leaves the mega panel (Tab-out)
      megaWrap.addEventListener('focusout', function (e) {
        if (!megaOpen) return;
        var next = e.relatedTarget;
        if (!next || (!megaWrap.contains(next) && next !== megaToggle)) {
          closeMega();
        }
      });

      // Reposition on resize
      window.addEventListener('resize', function () {
        if (megaOpen) { positionMega(); positionBridge(); }
      });
    }

    /* ── DROPDOWN KEYBOARD SUPPORT ─────────────────────────────── */
    $$('.csh__link--dropdown-toggle', header).forEach(function (btn) {
      var item    = btn.closest('.csh__item--has-dropdown');
      var dropdown = item ? $('.csh__dropdown', item) : null;

      btn.addEventListener('click', function () {
        var isExpanded = btn.getAttribute('aria-expanded') === 'true';
        // Close all others first
        $$('.csh__link--dropdown-toggle[aria-expanded="true"]', header).forEach(function (other) {
          if (other !== btn) other.setAttribute('aria-expanded', 'false');
        });
        btn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      });

      // Close on outside click
      document.addEventListener('click', function (e) {
        if (!item || !item.contains(e.target)) {
          btn.setAttribute('aria-expanded', 'false');
        }
      });

      // Close on Escape
      if (dropdown) {
        dropdown.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') {
            btn.setAttribute('aria-expanded', 'false');
            btn.focus();
          }
        });
      }
    });

    /* ── ACCOUNT PANEL ─────────────────────────────────────────── */
    var accountToggle = $('[data-csh-account-toggle]', header);
    var accountPanel  = $('[data-csh-account-panel]', header);

    if (accountToggle && accountPanel) {

      /* All focusable elements inside the panel */
      function panelFocusables() {
        return Array.from(accountPanel.querySelectorAll(
          'a, button, input, select, textarea, [tabindex]'
        ));
      }

      /* Disable / enable tab access to panel contents */
      function setPanelTabindex(enabled) {
        panelFocusables().forEach(function (el) {
          el.setAttribute('tabindex', enabled ? '0' : '-1');
        });
      }

      /* Start with panel inaccessible to keyboard */
      setPanelTabindex(false);

      function openAccountPanel() {
        accountPanel.classList.add('is-open');
        accountPanel.setAttribute('aria-hidden', 'false');
        accountToggle.setAttribute('aria-expanded', 'true');
        setPanelTabindex(true);
        /* Focus the email input (or first focusable) after transition */
        var firstInput = accountPanel.querySelector('input');
        var focusTarget = firstInput || panelFocusables()[0];
        if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 60);
      }

      function closeAccountPanel() {
        accountPanel.classList.remove('is-open');
        accountPanel.setAttribute('aria-hidden', 'true');
        accountToggle.setAttribute('aria-expanded', 'false');
        setPanelTabindex(false);
      }

      function isAccountPanelOpen() {
        return accountPanel.classList.contains('is-open');
      }

      accountToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        isAccountPanelOpen() ? closeAccountPanel() : openAccountPanel();
      });

      /* Close on outside click */
      document.addEventListener('click', function (e) {
        if (isAccountPanelOpen() && !accountPanel.contains(e.target) && e.target !== accountToggle) {
          closeAccountPanel();
        }
      });

      /* Close on Escape, return focus to toggle */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && isAccountPanelOpen()) {
          closeAccountPanel();
          accountToggle.focus();
        }
      });

      /* Prevent panel clicks from bubbling up and closing it */
      accountPanel.addEventListener('click', function (e) { e.stopPropagation(); });

      /* ── EMAIL FORM → validate then redirect to new customer accounts ── */
      var emailForm = $('[data-csh-account-email-form]', accountPanel);
      if (emailForm) {
        var emailInput = emailForm.querySelector('input[type="email"]');
        var emailError = emailInput
          ? document.getElementById(emailInput.getAttribute('aria-describedby'))
          : null;

        function showEmailError(msg) {
          if (!emailError) return;
          emailError.textContent = msg;
          emailError.removeAttribute('hidden');
          if (emailInput) {
            emailInput.setAttribute('aria-invalid', 'true');
            emailInput.focus();
          }
        }

        function clearEmailError() {
          if (!emailError) return;
          emailError.textContent = '';
          emailError.setAttribute('hidden', '');
          if (emailInput) emailInput.removeAttribute('aria-invalid');
        }

        /* Clear error as soon as the user starts correcting */
        if (emailInput) {
          emailInput.addEventListener('input', clearEmailError);
        }

        emailForm.addEventListener('submit', function (e) {
          e.preventDefault();
          var email = emailInput ? emailInput.value.trim() : '';

          /* Basic format check */
          var valid = email.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
          if (!valid) {
            showEmailError('Please enter a valid email address.');
            return;
          }

          clearEmailError();
          window.location.href = 'https://account.loganhollowell.com/?email=' + encodeURIComponent(email);
        });
      }
    }

    /* ── CART BUTTON → let lh-quick-cart.liquid handle via [data-js-cart-icon] ── */
    // No click handler here — lh-quick-cart.liquid's document click listener
    // watches for [data-js-cart-icon] and calls open(). Keeping logic in one place.

    /* ── CART COUNT ────────────────────────────────────────────── */
    function updateCartCount(count) {
      /* Visual badge */
      var countEls = $$('[data-js-cart-count]');
      countEls.forEach(function (el) {
        el.textContent = count > 0 ? count : '';
        el.classList.toggle('csh__cart-count--empty', !count);
      });

      /* Accessible label on the cart button (WCAG 4.1.2) */
      var label = count === 0 ? 'Cart, empty'
                : count === 1 ? 'Cart, 1 item'
                : 'Cart, ' + count + ' items';
      $$('[data-js-cart-btn]').forEach(function (btn) {
        btn.setAttribute('aria-label', label);
      });

      /* Live region announcement (WCAG 4.1.3) */
      var announcement = count === 0 ? 'Cart is now empty'
                       : count === 1 ? 'Cart updated: 1 item'
                       : 'Cart updated: ' + count + ' items';
      $$('[data-js-cart-live]').forEach(function (el) {
        el.textContent = announcement;
      });
    }

    // Listen for the theme's custom DOM event (fires when window.flu.states.useCustomEvents is true)
    document.addEventListener('cart:updated', function (e) {
      var detail = e.detail || {};
      // Theme fires { cart: { item_count } }; fall back to direct itemCount for other integrations
      var count = detail.cart ? detail.cart.item_count : detail.itemCount;
      updateCartCount(count);
    });

    // Fetch interceptor — reliable fallback regardless of useCustomEvents flag.
    // Watches change/update/clear endpoints which all return the full cart object.
    (function patchCartFetch() {
      var _fetch = window.fetch;
      window.fetch = function (url, opts) {
        var result = _fetch.apply(this, arguments);
        if (typeof url === 'string' && /\/cart\/(change|update|clear)\.js/.test(url)) {
          result.then(function (res) {
            return res.clone().json();
          }).then(function (cart) {
            if (typeof cart.item_count === 'number') {
              updateCartCount(cart.item_count);
            }
          }).catch(function () {});
        }
        return result;
      };
    })();

    // iOS bfcache: when the user presses Back, Safari restores the frozen DOM
    // (which has the old empty-badge state). Re-fetch cart count to sync.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        fetch('/cart.js')
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            if (typeof cart.item_count === 'number') {
              updateCartCount(cart.item_count);
            }
          })
          .catch(function () {});
      }
    });

    /* ── MOBILE DRAWER TOGGLE ──────────────────────────────────── */
    var menuBtn  = $('[data-js-menu-button]', header);
    var drawer   = document.querySelector('[data-drawer-menu]');
    var overlay  = drawer ? $('[data-overlay]', drawer) : null;

    if (menuBtn && drawer) {
      var drawerScrollPos = 0;

      function openDrawer() {
        drawer.classList.add('active');
        document.body.setAttribute('mobile-menu-open', 'true');
        menuBtn.setAttribute('aria-expanded', 'true');
        menuBtn.setAttribute('aria-label', menuBtn.getAttribute('data-aria-label-opened') || 'Close menu');
        setTimeout(function () {
          drawer.classList.add('visible');
          document.body.setAttribute('data-fluorescent-overlay-open', 'true');
          drawerScrollPos = window.pageYOffset;
          document.body.style.top = '-' + drawerScrollPos + 'px';
          document.body.classList.add('scroll-lock');
        }, 50);
      }

      function closeDrawer() {
        drawer.classList.remove('visible');
        document.body.setAttribute('mobile-menu-open', 'false');
        document.body.setAttribute('data-fluorescent-overlay-open', 'false');
        menuBtn.setAttribute('aria-expanded', 'false');
        menuBtn.setAttribute('aria-label', menuBtn.getAttribute('data-aria-label-closed') || 'Open menu');
        setTimeout(function () {
          drawer.classList.remove('active');
          document.body.classList.remove('scroll-lock');
          document.body.style.top = '';
          window.scrollTo(0, drawerScrollPos);
        }, 300);
      }

      menuBtn.addEventListener('click', function () {
        drawer.classList.contains('active') ? closeDrawer() : openDrawer();
      });

      if (overlay) {
        overlay.addEventListener('click', closeDrawer);
      }

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('active')) closeDrawer();
      });
    }

    /* ── HEADER HEIGHT CSS VAR ─────────────────────────────────── */
    function setHeightVar() {
      document.documentElement.style.setProperty(
        '--custom-header-height',
        header.offsetHeight + 'px'
      );
    }
    setHeightVar();
    window.addEventListener('resize', setHeightVar);
  }

  /* ──────────────────────────────────────────────────────────────
     BOOT
  ────────────────────────────────────────────────────────────── */
  onReady(function () {
    $$('[data-custom-header]').forEach(initHeader);
  });

  // Re-init after Shopify theme editor section events
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector('[data-custom-header]');
    if (el) initHeader(el);
  });

})();
